/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import Element from '../lib/chem/Element';

// Canonical chemical symbol for free-text input ("na" → "Na", " CL " → "Cl"),
// or null when the text is not a known element.
//
// Every basis atom ends up in `new Atom(...)`, whose constructor looks the
// symbol up with Element.getByName — and that call FABRICATES an "Unknown"
// element (atomic number 0, weight 0) for anything it does not know rather
// than failing. A zero mass is fatal to the MD: the thermostat and the
// integrator divide by it, every velocity goes infinite, and the lattice
// vanishes on the first step. A lowercase "na" survives the mass lookup (the
// table is case-insensitive) but misses every symbol-keyed table — element
// colors, force-field packs, formal charges — so it renders gray and runs on
// the generic fallback field. Both are caught here: the symbol is normalized
// to chemistry case and checked against the element table BEFORE it is
// written into a basis. D and T (deuterium / tritium) are accepted.
export const normalizeElementSymbol = (input: string | undefined | null): string | null => {
  if (!input) return null;
  const s = input.trim();
  if (!/^[A-Za-z]{1,2}$/.test(s)) return null;
  const key = s.toUpperCase();
  // Read the table directly, NOT through getByName: getByName inserts a
  // placeholder for unknown keys, which would make a second lookup "known".
  const el = (Element.ByName as Record<string, { number: number; weight: number } | undefined>)[key];
  if (!el || !(el.number > 0) || !(el.weight > 0)) return null;
  return key.length === 1 ? key : key[0] + key[1].toLowerCase();
};

export const isKnownElementSymbol = (input: string | undefined | null): boolean =>
  normalizeElementSymbol(input) !== null;
